import React, { type ComponentType } from 'react';
import { isFedRamp } from '@atlassian/atl-context';
import { AsyncJiraCommandPalette } from '@atlassian/jira-command-palette-integration/src/async.tsx';
import componentWithConditionOld from '@atlassian/jira-common-util-component-with-condition/src/index.tsx';
import FavoriteChangeImperativeApi from '@atlassian/jira-favourite-change-provider/src/view/imperative-api/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { AsyncFeedbackCollectorRenderer as FeedbackCollectorRenderer } from '@atlassian/jira-feedback-collector/src/ui/feedback-renderer/async.tsx';
import LoadGICResources from '@atlassian/jira-global-issue-create-resources/src/index.tsx';
import { InvitePeopleModal } from '@atlassian/jira-invite-people/src/ui/index.tsx';
import AsyncIssueArchivalModal from '@atlassian/jira-issue-archival-modal/src/async.tsx';
import { AsyncIssueCreateModal } from '@atlassian/jira-issue-create/src/async.tsx';
import { AsyncIssueTransitionModal } from '@atlassian/jira-issue-transition/src/index.tsx';
import LegacyAsyncModals from '@atlassian/jira-legacy-async-modals/src/index.tsx';
import AsyncNewIssueCreateTrigger from '@atlassian/jira-new-issue-create-trigger/src/async.tsx';
import CreateNewPlanWizard from '@atlassian/jira-portfolio-3-create-new-plan-wizard-drawer/src/ui/index.tsx';
import CreateSamplePlan from '@atlassian/jira-portfolio-3-create-sample-plan/src/ui/index.tsx';
import ProjectCreateDrawer from '@atlassian/jira-project-create-drawer/src/index.tsx';
import { gicOptInStatusResources } from '@atlassian/jira-router-resources-global-issue-create-switch/src/index.tsx';
import ShortcutsDialog from '@atlassian/jira-shortcuts-dialog/src/ui/index.tsx';
import { FlagIssueWithCommentModal } from '@atlassian/jira-software-add-issue-flag-with-comment/src/ui/index.tsx';
import { AsyncEnforceStorageLimitsModal } from '@atlassian/jira-software-enforce-storage-limits/src/ui/modal/async.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import AsyncAttachmentViewedStreamHubEvents from '@atlassian/jira-issue-attachment-viewed-streamhub-events/src/ui/async.tsx';
import { COMMAND_PALETTE_CONTAINER_TEST_ID } from './constants.tsx';

export const useIsModernGlobalIssueCreatePreferred = () =>
	useResource(gicOptInStatusResources).data?.gicOptInStatus?.isEnabled ?? false;

const LegacyComponents = () => {
	const locale = useLocale();

	return (
		<>
			<LegacyAsyncModals locale={locale} />
		</>
	);
};

const ProjectCreateDrawerComponent = () => {
	const appEditions = useAppEditions();
	const isAdmin = useIsAdmin();
	const locale = useLocale();

	return (
		<ProjectCreateDrawer
			isAdmin={isAdmin}
			isOnboarding={false}
			locale={locale}
			appEditions={appEditions}
		/>
	);
};

const ModernGlobalIssueCreate = componentWithConditionOld(
	() =>
		fg('render_modern_global_issue_create_modal_experience') &&
		useIsModernGlobalIssueCreatePreferred(),
	() => (
		<>
			<AsyncEnforceStorageLimitsModal />
			<AsyncIssueCreateModal />
			{fg('render_modern_global_issue_create_modal_experience') ? (
				<AsyncNewIssueCreateTrigger />
			) : null}
		</>
	),
	() => null,
);

const ModernIssueTransition = componentWithConditionOld(
	() => fg('render-modern-issue-transition') || isFedRamp(),
	AsyncIssueTransitionModal,
	() => <></>,
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Global: ComponentType<Record<any, any>> = () => (
	<>
		<FavoriteChangeImperativeApi />
		<LoadGICResources />
		<FeedbackCollectorRenderer />
		<LegacyComponents />
		<CreateNewPlanWizard />
		<CreateSamplePlan />
		<InvitePeopleModal />
		<ProjectCreateDrawerComponent />
		<AsyncIssueArchivalModal />
		<ModernGlobalIssueCreate />
		<ModernIssueTransition />
		<ShortcutsDialog />
		<div data-testid={COMMAND_PALETTE_CONTAINER_TEST_ID}>
			<AsyncJiraCommandPalette />
		</div>
		<FlagIssueWithCommentModal />
		<AsyncAttachmentViewedStreamHubEvents />
	</>
);

export default Global;
